// frontend/src/utils/safetyStockColDef.js
//
// Shared column-def builder for the safety-stock override cell, used by the
// Inventory grid and the Item Setup grid. Both render `safety_stock_units`
// with `OverrideCellRenderer`, and both ask the same two questions about it —
// the ink and the tooltip. (The "you can type here" box is not a question
// this cell answers: every editable cell of every grid gets it from
// utils/gridDefaults.js.)
//
// "Is this overridden?" is `safety_stock_user_modified`, straight from the
// column that decides it (backend/api/services/safety_stock.py); it ships
// read-only on both the Inventory and item-location wires. Never a comparison
// `safety_stock_units !== system_safety_stock`: a merchant who pins the buffer
// to the engine's current figure has still overridden it.

import OverrideCellRenderer from '../components/grid/OverrideCellRenderer';
import { overrideCellTooltip } from './overrideCellTooltip';
import { parseClearableUnitsCell } from './planningCellParsers';

const SAFETY_STOCK_FIELD = 'safety_stock_units';

const SAFETY_STOCK_GESTURES = {
  override: 'forecast.clickToOverride',
  reset: 'inventory.safetyStockClearToReset',
};

/**
 * True when a person put this number here, per the server.
 *
 * Exported because the tooltip and the ink below are not the only callers —
 * tests assert this predicate directly.
 */
export function isSafetyStockOverridden(row) {
  return Boolean(row?.safety_stock_user_modified);
}

/**
 * @param {object} opts
 * @param {(key: string) => string} opts.t  i18n lookup.
 * @param {(value: number, dp: number) => string} opts.formatNumber
 * @param {(raw: string) => number} opts.parseNumber  the page's LOCALE-AWARE
 *   parser (useFormatters), not the global `Number` — a merchant typing
 *   "1.234" means one thousand two hundred thirty-four in nl-NL.
 * @param {string} opts.headerName
 * @param {boolean | ((params: object) => boolean)} [opts.editable=true]
 * @param {(row: object) => boolean} [opts.notInUse]  true on rows whose
 *   inventory target mode does not read the safety stock at all (Days of
 *   supply — `utils/inventoryTargetMode.readsSafetyStock`). Such a cell is
 *   dimmed and says why, the mirror of how the Days of supply target is
 *   treated under Safety stock (pages/itemPlanningColumns.js). Opt-in: a grid
 *   whose rows do not carry the effective mode cannot answer the question,
 *   and must not guess at it.
 * @returns {object} Partial column def; the caller adds width/pinning/etc.
 */
export function safetyStockColDef({
  t, formatNumber, parseNumber, headerName,
  editable = true,
  notInUse,
}) {
  if (typeof parseNumber !== 'function') {
    throw new Error('safetyStockColDef: parseNumber is required');
  }

  return {
    field: SAFETY_STOCK_FIELD,
    headerName,
    headerTooltip: t('common.tooltips.safetyStock'),
    editable,
    type: 'numberColumn',
    cellRenderer: OverrideCellRenderer,
    cellRendererParams: {
      getSystemValue: (data) => data.system_safety_stock,
      isModified: (data) => isSafetyStockOverridden(data),
    },
    valueParser: (params) => parseClearableUnitsCell(params, parseNumber),
    tooltipValueGetter: (params) => {
      // Inert under Days of supply: say so instead of inviting an override
      // that would change no plan. The statement outranks the two action
      // lines below — "clear to reset" is true and useless on a cell the
      // engine is not reading.
      const notRead = notInUse && notInUse(params.data)
        ? t('common.safetyStockNotRead')
        : '';
      const systemValue = params.data.system_safety_stock;
      // No engine figure yet — the classification run has not visited this
      // pair. There is nothing to compare against and nothing to restore, so
      // neither the "click to override" nor the "clear to reset" line would be
      // true.
      if (systemValue === null || systemValue === undefined) return notRead;
      const label = `${t('inventory.safetyStockSuggested')}: ${formatNumber(systemValue, 0)}`;
      if (notRead) return `${label}\n${notRead}`;
      return overrideCellTooltip(label, isSafetyStockOverridden(params.data), t, SAFETY_STOCK_GESTURES);
    },
    // The ink (OverrideCellRenderer) is the override marker; no cell class
    // says it again.
    cellClassRules: {
      ...(notInUse ? {
        'ag-cell-not-in-use': (params) => notInUse(params.data),
      } : {}),
    },
  };
}
